import { useState } from 'react';

type OutcomeType =
  | 'resolved'
  | 'unresolved_abandoned_by_customer'
  | 'unresolved_attendant_gave_up'
  | 'unresolved_blocked_by_system'
  | 'unresolved_out_of_scope'
  | 'not_applicable';

const OUTCOME_OPTIONS: { value: OutcomeType; label: string }[] = [
  { value: 'resolved', label: 'Resolvido' },
  { value: 'unresolved_abandoned_by_customer', label: 'Cliente abandonou (não resolvido)' },
  { value: 'unresolved_attendant_gave_up', label: 'Atendente desistiu (não resolvido)' },
  { value: 'unresolved_blocked_by_system', label: 'Bloqueado pelo sistema (não resolvido)' },
  { value: 'unresolved_out_of_scope', label: 'Fora do escopo (não resolvido)' },
  { value: 'not_applicable', label: 'Não aplicável (teste/interno)' },
];

/** ADR-150 — follow-up manual agendado junto do encerramento (fonte `manual` do motor SOA). */
export interface CloseFollowupRequest {
  waitDays: number;
  content: string;
  mode: 'H_a' | 'H_b' | 'H_c';
}

const FOLLOWUP_PRESETS: { value: number; label: string }[] = [
  { value: 1, label: 'Amanhã (1 dia)' },
  { value: 3, label: 'Em 3 dias' },
  { value: 7, label: 'Em 1 semana' },
  { value: 30, label: 'Em 1 mês' },
];

const FOLLOWUP_MODES: { value: CloseFollowupRequest['mode']; label: string }[] = [
  { value: 'H_b', label: 'IA envia e aguarda (resposta cai no inbox)' },
  { value: 'H_c', label: 'IA envia e conduz a conversa' },
  { value: 'H_a', label: 'Eu mesmo envio (vira pendência na data)' },
];

interface ConversationCloseModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: {
    outcomeType: OutcomeType;
    outcomeReason?: string;
    notes?: string;
    overrideJustification?: string;
    followup?: CloseFollowupRequest;
  }) => Promise<void>;
  /** Quando true, soft-warning ativo — modal pede overrideJustification opcional. */
  hasSoftWarning?: boolean;
  /** ADR-150 — exibe a seção "Agendar follow-up" (cap `initiate-proactive-conversation`). */
  followupEnabled?: boolean;
  /** Erros de bloqueio HARD vindos da última tentativa (renderiza em destaque). */
  blockerError?: string;
  submitting?: boolean;
}

/**
 * ConversationCloseModal — Fatia F (ADR-054 §F4.2).
 *
 * Modal de fechamento estruturado: dropdown outcomeType (6 opções) +
 * textarea reason (opcional) + notes (opcional) + overrideJustification
 * (visível quando hasSoftWarning).
 */
export function ConversationCloseModal({
  open,
  onClose,
  onSubmit,
  hasSoftWarning,
  followupEnabled,
  blockerError,
  submitting,
}: ConversationCloseModalProps): JSX.Element | null {
  const [outcomeType, setOutcomeType] = useState<OutcomeType>('resolved');
  const [outcomeReason, setOutcomeReason] = useState('');
  const [notes, setNotes] = useState('');
  const [overrideJustification, setOverrideJustification] = useState('');
  // ADR-150 — follow-up manual opcional ("te procuro semana que vem").
  const [followupOn, setFollowupOn] = useState(false);
  const [followupWaitDays, setFollowupWaitDays] = useState(7);
  const [followupContent, setFollowupContent] = useState('');
  const [followupMode, setFollowupMode] = useState<CloseFollowupRequest['mode']>('H_b');

  if (!open) return null;

  const followupActive = followupEnabled === true && followupOn;
  // H_a não envia texto (vira pendência) — só H_b/H_c exigem a mensagem.
  const followupMissingContent =
    followupActive && followupMode !== 'H_a' && followupContent.trim().length === 0;

  const handleSubmit = async (): Promise<void> => {
    if (followupMissingContent) return;
    await onSubmit({
      outcomeType,
      ...(outcomeReason.length > 0 && { outcomeReason }),
      ...(notes.length > 0 && { notes }),
      ...(overrideJustification.length >= 10 && { overrideJustification }),
      ...(followupActive && {
        followup: {
          waitDays: followupWaitDays,
          content: followupContent.trim(),
          mode: followupMode,
        },
      }),
    });
  };

  return (
    <div
      role="dialog"
      aria-label="Fechar conversa"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      {/* Fase C Brief 2A r7b — DS tokens via inline style (bg-surface) + DS classes. */}
      <div
        className="w-full max-w-lg rounded-lg p-6 shadow-xl"
        style={{ background: 'var(--bg-surface)' }}
      >
        <h2 className="mb-4 text-lg font-semibold">Fechar conversa</h2>

        {blockerError !== undefined && blockerError.length > 0 ? (
          <div
            className="mb-4 rounded border p-3 text-sm"
            style={{
              borderColor: 'var(--danger-border)',
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
            }}
          >
            {blockerError}
          </div>
        ) : null}

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Resultado</span>
          <select
            className="w-full rounded border px-2 py-1"
            style={{
              background: 'var(--bg-surface-2)',
              borderColor: 'var(--border-default)',
            }}
            value={outcomeType}
            onChange={(e): void => setOutcomeType(e.target.value as OutcomeType)}
            disabled={submitting}
          >
            {OUTCOME_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Motivo (opcional)</span>
          <textarea
            className="w-full rounded border px-2 py-1"
            style={{
              background: 'var(--bg-surface-2)',
              borderColor: 'var(--border-default)',
            }}
            rows={2}
            value={outcomeReason}
            onChange={(e): void => setOutcomeReason(e.target.value)}
            disabled={submitting}
            maxLength={2000}
          />
        </label>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Notas internas (opcional)</span>
          <textarea
            className="w-full rounded border px-2 py-1"
            style={{
              background: 'var(--bg-surface-2)',
              borderColor: 'var(--border-default)',
            }}
            rows={2}
            value={notes}
            onChange={(e): void => setNotes(e.target.value)}
            disabled={submitting}
            maxLength={2000}
          />
        </label>

        {followupEnabled === true ? (
          <div
            className="mb-3 rounded border p-3"
            style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface-2)' }}
          >
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={followupOn}
                onChange={(e): void => setFollowupOn(e.target.checked)}
                disabled={submitting}
              />
              <span className="text-sm font-medium">Agendar follow-up após encerrar</span>
            </label>

            {followupOn ? (
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium">Quando</span>
                  <select
                    className="w-full rounded border px-2 py-1"
                    style={{
                      background: 'var(--bg-surface)',
                      borderColor: 'var(--border-default)',
                    }}
                    value={followupWaitDays}
                    onChange={(e): void => setFollowupWaitDays(Number(e.target.value))}
                    disabled={submitting}
                  >
                    {FOLLOWUP_PRESETS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="mb-1 block text-sm font-medium">Como</span>
                  <select
                    className="w-full rounded border px-2 py-1"
                    style={{
                      background: 'var(--bg-surface)',
                      borderColor: 'var(--border-default)',
                    }}
                    value={followupMode}
                    onChange={(e): void =>
                      setFollowupMode(e.target.value as CloseFollowupRequest['mode'])
                    }
                    disabled={submitting}
                  >
                    {FOLLOWUP_MODES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>

                {followupMode !== 'H_a' ? (
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium">Mensagem do follow-up</span>
                    <textarea
                      className="w-full rounded border px-2 py-1"
                      style={{
                        background: 'var(--bg-surface)',
                        borderColor: followupMissingContent
                          ? 'var(--warning-border)'
                          : 'var(--border-default)',
                      }}
                      rows={2}
                      value={followupContent}
                      onChange={(e): void => setFollowupContent(e.target.value)}
                      disabled={submitting}
                      maxLength={2000}
                      placeholder='Ex.: "Oi! Como combinado, voltando para saber se está tudo certo por aí."'
                    />
                    {followupMissingContent ? (
                      <span className="mt-1 block text-xs" style={{ color: 'var(--warning-fg)' }}>
                        Escreva a mensagem que será enviada no follow-up.
                      </span>
                    ) : null}
                  </label>
                ) : (
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium">
                      Anotação para a pendência (opcional)
                    </span>
                    <textarea
                      className="w-full rounded border px-2 py-1"
                      style={{
                        background: 'var(--bg-surface)',
                        borderColor: 'var(--border-default)',
                      }}
                      rows={2}
                      value={followupContent}
                      onChange={(e): void => setFollowupContent(e.target.value)}
                      disabled={submitting}
                      maxLength={2000}
                      placeholder="O que você planeja dizer quando retomar o contato"
                    />
                  </label>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {hasSoftWarning === true ? (
          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium" style={{ color: 'var(--warning-fg)' }}>
              ⚠️ Checklist crítico pendente — justificativa de override
            </span>
            <textarea
              className="w-full rounded border px-2 py-1"
              style={{
                background: 'var(--bg-surface-2)',
                borderColor: 'var(--warning-border)',
              }}
              rows={2}
              value={overrideJustification}
              onChange={(e): void => setOverrideJustification(e.target.value)}
              disabled={submitting}
              minLength={10}
              maxLength={2000}
              placeholder="Explique por que está fechando mesmo com checklist crítico pendente (mínimo 10 caracteres)"
            />
          </label>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--border-default)' }}
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={(): void => {
              void handleSubmit();
            }}
            disabled={submitting === true || followupMissingContent}
          >
            {submitting === true ? 'Fechando…' : 'Fechar conversa'}
          </button>
        </div>
      </div>
    </div>
  );
}
